import { __assign, __read } from "tslib";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { View, PickerView as TaroPickerView, PickerViewColumn as TaroPickerViewColumn, } from "../_core/components";
import { PickerView as CorePickerView, PickerViewColumn as CorePickerViewColumn, } from "../_core/components/picker";
import { useDefaultValue } from "../_util/use-default";
import { Popup } from "../popup";
import { dataToString, dataToIndexes, getFieldRanges, indexesToData, stringToData, adjustData, fieldsOrder, fieldUnitLocaleKeyMap, } from "./util";
import { noop } from "../_util/noop";
import { TaroEnv } from "../_util/env";
import { useTranslation } from "../config-provider/i18n";
import { useConfig } from "../_util/use-config";
dayjs.extend(customParseFormat);
var PickerView = TaroEnv === "weapp" ? TaroPickerView : CorePickerView;
var PickerViewColumn = TaroEnv === "weapp" ? TaroPickerViewColumn : CorePickerViewColumn;
var defaultFormatMap = {
    year: "YYYY",
    month: "YYYY-MM",
    day: "YYYY-MM-DD",
    date: "YYYY-MM-DD",
    time: "HH:mm",
    datetime: "YYYY-MM-DD HH:mm",
};
export function DatePicker(props) {
    var classPrefix = useConfig().classPrefix;
    var t = useTranslation();
    var _a = useDefaultValue(props), header = _a.header, footer = _a.footer, disabled = _a.disabled, value = _a.value, onChange = _a.onChange, children = _a.children, className = _a.className, _b = _a.style, style = _b === void 0 ? {} : _b, indicatorClassName = _a.indicatorClassName, indicatorStyle = _a.indicatorStyle, start = _a.start, end = _a.end, _c = _a.mode, mode = _c === void 0 ? "date" : _c, _d = _a.format, format = _d === void 0 ? defaultFormatMap[mode] || defaultFormatMap.date : _d, _e = _a.onCancel, onCancel = _e === void 0 ? noop : _e, _f = _a.step, step = _f === void 0 ? {} : _f;
    useEffect(function () {
        if (value && !dayjs(value, format, true).isValid()) {
            throw new Error("`value`[" + format + "](" + value + ") is not valid");
        }
        if (start && !dayjs(start, format, true).isValid()) {
            throw new Error("`start`[" + format + "](" + start + ") is not valid");
        }
        if (end && !dayjs(end, format, true).isValid()) {
            throw new Error("`end`[" + format + "](" + end + ") is not valid");
        }
    }, [value, format, start, end]);
    var _g = __read(useState(stringToData(value || end || start, format, step)), 2), innerValue = _g[0], setInnerValue = _g[1];
    var _h = __read(useState(false), 2), confirmDisabled = _h[0], setConfirmDisabled = _h[1];
    useEffect(function () {
        if (value !== dataToString(innerValue, format)) {
            setInnerValue(stringToData(value || end || start, format, step));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    var ranges = useMemo(function () { return getFieldRanges(innerValue, format, start, end); }, [
        innerValue,
        format,
        start,
        end,
    ]);
    if (disabled) {
        return React.createElement(React.Fragment, null, children);
    }
    return (React.createElement(Popup, { onClose: function (_a) {
            var source = _a.source, event = _a.event;
            if (source === "mask") {
                onCancel(event);
            }
        }, onVisibleChange: function (visible) {
            if (visible) {
                setConfirmDisabled(false);
            }
        }, overlay: function (close) { return (React.createElement(View, { className: classNames(classPrefix + "-picker", className), 
            // 兼容旧的样式
            style: __assign(__assign({}, style), { display: "block" }) },
            React.createElement(View, { className: classPrefix + "-picker-inner" },
                React.createElement(View, { className: classPrefix + "-picker__header" },
                    React.createElement(View, { className: classPrefix + "-picker__cancel", onClick: function (event) {
                            setInnerValue(stringToData(value || end || start, format, step));
                            onCancel(event);
                            close();
                        } }, t.cancelText),
                    React.createElement(View, { className: classPrefix + "-picker__title" }, header),
                    React.createElement(View, { className: classNames(classPrefix + "-picker__confirm", {
                            "is-disabled": confirmDisabled,
                        }), onClick: function (event) {
                            if (confirmDisabled) {
                                return;
                            }
                            onChange(dataToString(innerValue, format), { event: event });
                            close();
                        } }, t.confirmText)),
                React.createElement(View, { className: classPrefix + "-picker__body" },
                    React.createElement(PickerView, { className: classPrefix + "-picker__body-inner", value: dataToIndexes(innerValue, ranges, step), onChange: function (event) {
                            // 使用滚动前的 ranges 生成 data，该 data 可能处于不合法日期范围
                            var data = indexesToData(event.detail.value, ranges, step);
                            setInnerValue(adjustData(data, format, start, end));
                        }, onPickStart: function () { return setConfirmDisabled(true); }, onPickEnd: function () { return setConfirmDisabled(false); }, indicatorStyle: indicatorStyle, indicatorClass: indicatorClassName }, fieldsOrder
                        .filter(function (field) { return ranges[field]; })
                        .map(function (field) { return (React.createElement(PickerViewColumn, { className: classPrefix + "-picker__item", key: field }, Array(ranges[field][1] - ranges[field][0] + 1)
                        .fill(null)
                        .filter(function (_, index) { return index % ((step === null || step === void 0 ? void 0 : step[field]) || 1) === 0; })
                        .map(function (_, index) { return (React.createElement(View, { className: classPrefix + "-picker__item-inner", key: ranges[field][0] + "-" + ranges[field][1] + "-" + index },
                        ranges[field][0] -
                            (ranges[field][0] % ((step === null || step === void 0 ? void 0 : step[field]) || 1)) +
                            index * ((step === null || step === void 0 ? void 0 : step[field]) || 1),
                        " ",
                        t[fieldUnitLocaleKeyMap[field]])); }))); }))),
                footer))); } }, children));
}
//# sourceMappingURL=DatePicker.js.map